import { FC, useContext, useEffect, useRef, useState } from "react";
import { ExportFormatType } from "@app/exports/enumerations";
import { ExportBulkSpecialtyGroupExceptionsModel } from "@app/exports/exportModels";
import { Modal } from "@app/shared/Modals";
import { toastInfo } from "@app/exceptions/ReportQueuedExceptionToast";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import ExportService from "@app/exports/exportService";

interface ExceptionsDownloadAllModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
}

const ExceptionsDownloadAllModal: FC<ExceptionsDownloadAllModalProps> = ({ isModalOpen, setIsModalOpen }) => {
  const exceptionsFilterContext = useContext(ExceptionsFiltersContext);
  const defaultExportNamePrefix = exceptionsFilterContext.isForArchived ? "ArchivedVolumeExceptions" : "VolumeExceptions";
  const [exportName, setExportName] = useState<string>(helpers.reportName(defaultExportNamePrefix));
  const [reportType, setReporType] = useState<ExportFormatType>(ExportFormatType.CSV);
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);

  const exportService = useRef(new ExportService()).current;

  useEffect(() => {
    if (isModalOpen) {
      setExportName(helpers.reportName(defaultExportNamePrefix));
    }
  }, [isModalOpen, defaultExportNamePrefix]);

  const onReportTypeChange = (value: string) => {
    setReporType(value === 'CSV' ? ExportFormatType.CSV : ExportFormatType.Excel);
  }

  const resetFormValues = () => {
    setExportName(helpers.reportName(defaultExportNamePrefix));
    setReporType(ExportFormatType.CSV);
    setEmailOnSuccess(true);
  }

  const generateBulkExceptionReport = () => {
    const requestModel: ExportBulkSpecialtyGroupExceptionsModel = {
      eMailOnSuccess: emailOnSuccess,
      exportName: exportName,
      filters: exceptionsFilterContext.requestModel,
      formatType: reportType,
      runAsynchronous: true
    };

    exportService.generateBulkSpecialtyGroupExceptionReport(requestModel).then(() => {
      toastInfo(exportName, () => { });
      resetFormValues();
      setIsModalOpen(false);
    });
  }

  const closeModal = () => {
    resetFormValues();
    setIsModalOpen(false);
  }

  return (
    <Modal
      closeModal={closeModal}
      content={{
        body: (
          <>
            <div className="ps-3 pe-3 mb-3">
              <QESTextInput
                testId="bulk-exceptions__download-all-modal-input"
                label="Report Name"
                required={true}
                value={exportName}
                isValid={!!exportName}
                onChange={(e) => setExportName((e.target as HTMLInputElement).value)}
              />
            </div>

            <div className="ps-3 pe-3 mb-3">
              <p className="qes-text-input__label">Report Type</p>
              <div className="mb-2">
                <QESRadioButton
                  label="CSV File (.csv)"
                  name="report-type"
                  value="CSV"
                  checked={reportType === ExportFormatType.CSV}
                  onChange={(e) => onReportTypeChange((e.target as HTMLInputElement).value)}
                  id="download-all-modal-format-csv"
                  testId="bulk-exceptions__download-all-modal-format-csv"
                />
                <QESRadioButton
                  label="Microsoft Excel (.xlsx)"
                  name="report-type"
                  value="Excel"
                  checked={reportType === ExportFormatType.Excel}
                  onChange={(e) => onReportTypeChange((e.target as HTMLInputElement).value)}
                  id="download-all-modal-format-excel"
                  testId="bulk-exceptions__download-all-modal-format-excel"
                />
              </div>
            </div>

            <div className="ps-3 pe-3 mb-3">
              <QESCheckbox
                label="Alert me when the report is ready to view"
                checked={emailOnSuccess}
                onChange={(e) => setEmailOnSuccess((e.target as HTMLInputElement).checked)}
                id="download-all-modal__email-me"
                testId="bulk-exceptions__download-all-modal__email-me"
              />
            </div>
          </>
        ),
        cancelButtonText: "Cancel",
        confirmButtonAction: generateBulkExceptionReport,
        confirmButtonDisable: !exportName,
        confirmButtonText: "Generate Report",
        confirmButtonType: "primary",
        title: "Report",
      }}
      isVisible={isModalOpen}
      hideBodyScroll={true}
    />
  );
}

export default ExceptionsDownloadAllModal;
